import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { motion } from 'framer-motion';
import { css } from 'twin.macro';
import { StyledEventsActionbar } from './EventsActionbar.style';
import { getDefaultFilter, isNoFilter } from '../../libs/eventsFilters';
import WeekNavigation from '../../components/WeekNavigation/WeekNavigation';
import EventsFilterList from '../EventsFilterList';
import TruncateButton from '../../components/Button/TruncateButton';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import { NavigationVariants } from '../../libs/animations';
import debounce from '../../libs/debounce';
import { todayDateObject } from '../../libs/dateTimeUtilities';

const EventsActionbar = ({
  user,
  currentWeek,
  goToPreviousWeek,
  goToNextWeek,
  setUserPreferenceEventFilters,
  dateRangeObjectArray,
  onChangeDateRange,
  showDateRangePicker,
  showFilters,
  toggleShowFilters,
  onWeekNameClick,
  eventsFilterListUseMotion = true,
  ref = null,
  datepickerDropdownRef,
  filtersDropdownRef,
  actionbarDropdownHeight = 0,
  openFilterEditor
}) => {
  const { eventsFiltersApp } = user.preferences;
  const activeFilter = getDefaultFilter(eventsFiltersApp);

  function setFilterActive(aFilter) {
    const filterIndex = eventsFiltersApp.findIndex((f) => isEqual(f, aFilter));
    /* Make sure this is not done on the original objects, otherwise the EventsContainer
      would not detect a change and will not fetch the events again */
    const updatedFilters = eventsFiltersApp.map((filter) => ({
      ...filter,
      isDefault: false
    }));
    if (filterIndex > -1) updatedFilters[filterIndex].isDefault = true;
    setUserPreferenceEventFilters(updatedFilters);
  }

  const debouncedSetFilterActive = useMemo(
    () => debounce(setFilterActive),
    [eventsFiltersApp]
  );

  function openFilterEditorForFilter(filterIndex, editExistingFilter = false) {
    /* editExistingFilter will determine what happens when you click on cancel in the popover */
    openFilterEditor(filterIndex, editExistingFilter);
  }

  const debouncedOpenFilterEditor = useMemo(
    () => debounce(openFilterEditorForFilter),
    [eventsFiltersApp]
  );

  return (
    <div ref={ref}>
      <ContainerWrapper
        css={css`
          padding-bottom: ${actionbarDropdownHeight}px;
        `}
      >
        <StyledEventsActionbar>
          <div tw="min-w-0 -ml-4">
            <TruncateButton
              contentType="toggle"
              icon={isNoFilter(activeFilter) ? 'filter-list-off' : 'filter'}
              iconPosition="left"
              color={showFilters || !isNoFilter(activeFilter) ? 'primary' : 'default'}
              onClick={toggleShowFilters}
              dataAttributes={{ 'data-test-toggle-filters': true }}
            >
              {isNoFilter(activeFilter) ? i18n('No Filter') : activeFilter.name}
            </TruncateButton>
            {showFilters && (
              <div tw="absolute w-full left-0 mt-4" ref={filtersDropdownRef}>
                <EventsFilterList
                  filters={eventsFiltersApp}
                  setUserPreferenceEventFilters={setUserPreferenceEventFilters}
                  openFilterEditor={debouncedOpenFilterEditor}
                  activeFilter={activeFilter}
                  handleSetFilterActive={debouncedSetFilterActive}
                  useMotion={eventsFilterListUseMotion}
                />
                <div tw="w-full bg-silver-lighten-30 h-16" />
              </div>
            )}
          </div>
          <div tw="-mr-4">
            <WeekNavigation
              currentWeek={currentWeek}
              goToPreviousWeek={goToPreviousWeek}
              goToNextWeek={goToNextWeek}
              onWeekNameClick={onWeekNameClick}
              datepickerIsOpen={showDateRangePicker}
            />
            {showDateRangePicker && (
              <div tw="absolute w-full left-0 mt-4" ref={datepickerDropdownRef}>
                <motion.div
                  initial="toggleFromTopInvisible"
                  animate="toggleFromTopVisible"
                  exit="toggleFromTopInvisible"
                  variants={NavigationVariants}
                >
                  <div tw="bg-white w-full">
                    <ContainerWrapper data-test-events-daterangepicker>
                      <DateRangePicker
                        currentWeek={currentWeek}
                        dateRange={dateRangeObjectArray}
                        onChange={onChangeDateRange}
                        showDateRangePicker={showDateRangePicker}
                        minDate={todayDateObject().subtract(3, 'y')}
                        maxDate={todayDateObject().add(10, 'y')}
                      />
                    </ContainerWrapper>
                  </div>
                  <div tw="w-full bg-silver-lighten-30 h-24" />
                </motion.div>
              </div>
            )}
          </div>
        </StyledEventsActionbar>
      </ContainerWrapper>
    </div>
  );
};

EventsActionbar.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    preferences: PropTypes.shape({
      eventsFiltersApp: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired
  }).isRequired,
  setUserPreferenceEventFilters: PropTypes.func.isRequired,
  currentWeek: PropTypes.shape({
    start: PropTypes.shape({}),
    end: PropTypes.shape({})
  }).isRequired,
  goToPreviousWeek: PropTypes.func.isRequired,
  goToNextWeek: PropTypes.func.isRequired,
  onChangeDateRange: PropTypes.func.isRequired,
  dateRangeObjectArray: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showDateRangePicker: PropTypes.bool.isRequired,
  showFilters: PropTypes.bool.isRequired,
  toggleShowFilters: PropTypes.func.isRequired,
  onWeekNameClick: PropTypes.func.isRequired,
  eventsFilterListUseMotion: PropTypes.bool,
  /** Forwarded to the actionbar's outer element, used to measure its height */
  ref: PropTypes.shape({}),
  filtersDropdownRef: PropTypes.shape({}).isRequired,
  datepickerDropdownRef: PropTypes.shape({}).isRequired,
  actionbarDropdownHeight: PropTypes.number,
  openFilterEditor: PropTypes.func.isRequired
};

export default EventsActionbar;
